import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import * as api from "./api";
import type { BenchmarksParams, EzConfigDto, FeedParams, UserDecision } from "./types";

export const ezKeys = {
  feed: (params: FeedParams) => ["ezamowienia", "feed", params] as const,
  proceeding: (id: string) => ["ezamowienia", "proceeding", id] as const,
  preview: ["ezamowienia", "preview"] as const,
  windows: ["ezamowienia", "windows"] as const,
  config: ["ezamowienia", "config"] as const,
};

export function useEzFeed(params: FeedParams) {
  return useQuery({
    queryKey: ezKeys.feed(params),
    queryFn: () => api.getFeed(params),
  });
}

export function useEzProceeding(id: string) {
  return useQuery({
    queryKey: ezKeys.proceeding(id),
    queryFn: () => api.getProceeding(id),
    enabled: !!id,
  });
}

export function useEzAsk() {
  return useMutation({
    mutationFn: ({
      question,
      history,
      model,
    }: {
      question: string;
      history: { role: string; content: string }[];
      model?: string;
    }) => api.postAsk(question, history, model),
  });
}

export function useEzInsights() {
  return useQuery({ queryKey: ["ezamowienia", "insights"] as const, queryFn: api.getInsights });
}

export function useEzGenerateInsight() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.postInsight,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ezamowienia", "insights"] }),
  });
}

export function useEzEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      documentId,
      fileName,
    }: {
      id: string;
      documentId: string;
      fileName: string;
    }) => api.postEstimate(id, documentId, fileName),
    onSuccess: (_r, vars) =>
      qc.invalidateQueries({ queryKey: ezKeys.proceeding(vars.id) }),
  });
}

export function useEzDocuments(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["ezamowienia", "documents", id] as const,
    queryFn: () => api.getDocuments(id),
    enabled,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });
}

/* Browser-agent runs for one proceeding. Polls only while a run is in flight —
   a run takes tens of seconds, so the card must move on its own. When the run
   stops running, the document list is refetched: the files only exist once the
   agent finished, and useEzDocuments caches for 10 minutes, so without this the
   card sits on "0 dokumentów" while 37 files wait in storage. */
export function useEzBrowserRuns(id: string, enabled: boolean) {
  const qc = useQueryClient();
  const wasRunning = useRef(false);
  const query = useQuery({
    queryKey: ["ezamowienia", "browser-runs", id] as const,
    queryFn: () => api.getBrowserRuns(id),
    enabled,
    refetchInterval: (q) =>
      (q.state.data ?? []).some((r) => r.status === "running") ? 3000 : false,
  });

  const running = (query.data ?? []).some((r) => r.status === "running");
  useEffect(() => {
    if (wasRunning.current && !running) {
      qc.invalidateQueries({ queryKey: ["ezamowienia", "documents", id] });
      qc.invalidateQueries({ queryKey: ezKeys.proceeding(id) });
    }
    wasRunning.current = running;
  }, [running, id, qc]);

  return query;
}

/* Ask EZ-ARON for a verdict on one proceeding. The task writes the result onto
   the proceeding, so we poll its detail until the recommendation appears. */
export function useEzRecommend() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.postRecommend(id),
    onSuccess: (_r, id) => {
      const stop = Date.now() + 120_000;
      const tick = () => {
        qc.invalidateQueries({ queryKey: ezKeys.proceeding(id) });
        qc.invalidateQueries({ queryKey: ["ezamowienia", "feed"] });
        if (Date.now() < stop) setTimeout(tick, 4000);
      };
      setTimeout(tick, 4000);
    },
  });
}

export function useEzBrowserFetch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.postBrowserFetch(id),
    onSuccess: (_r, id) => {
      qc.invalidateQueries({ queryKey: ["ezamowienia", "browser-runs", id] });
      qc.invalidateQueries({ queryKey: ["ezamowienia", "documents", id] });
    },
  });
}

export function useEzSimilar(id: string, enabled: boolean) {
  return useQuery({
    queryKey: ["ezamowienia", "similar", id] as const,
    queryFn: () => api.getSimilar(id),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useEzDecision() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: UserDecision }) =>
      api.postDecision(id, decision),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ezamowienia"] }),
  });
}

export function useEzBenchmarks(params: BenchmarksParams) {
  return useQuery({
    queryKey: ["ezamowienia", "benchmarks", params] as const,
    queryFn: () => api.getBenchmarks(params),
  });
}

export function useEzPreview() {
  return useQuery({ queryKey: ezKeys.preview, queryFn: api.getCandidatesPreview });
}

export function useEzAnalyze() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.postAnalyze,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ezamowienia"] }),
  });
}

export function useEzHistoricalLoad() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: api.postHistoricalLoad,
    onSuccess: () => qc.invalidateQueries({ queryKey: ezKeys.windows }),
  });
}

export function useEzIngestWindows() {
  return useQuery({
    queryKey: ezKeys.windows,
    queryFn: api.getIngestWindows,
    refetchInterval: 15000,
  });
}

export function useEzConfig() {
  return useQuery({ queryKey: ezKeys.config, queryFn: api.getConfig });
}

export function useEzUpdateConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Partial<EzConfigDto>) => api.putConfig(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ezamowienia"] }),
  });
}
